<script setup lang="ts">
import { computed, useAttrs } from 'vue'

const props = defineProps<{
  header?: boolean
  content?: boolean
  divide?: boolean
  disabled?: boolean
  href?: string
  target?: string
}>()

const emit = defineEmits<{
  (e: 'click', event: MouseEvent | KeyboardEvent): void
}>()

const attrs = useAttrs()
const classes = computed(() => [
  attrs.class,
  {
    disabled: props.disabled,
    'dropdown-header': props.header,
  },
])

const SAFE_HREF_PATTERN = /^(https?:|mailto:|tel:|\/(?!\/)|#)/i

const safeHref = computed(() => {
  const href = props.href?.trim()

  return href && SAFE_HREF_PATTERN.test(href) ? href : undefined
})

function handleClick(event: MouseEvent | KeyboardEvent) {
  if (!safeHref.value) {
    event.preventDefault()
  }

  if (props.disabled) {
    event.preventDefault()
    event.stopPropagation()

    return
  }

  emit('click', event)
}
</script>

<template>
  <li v-if="content || $slots.content">
    <slot />
    <slot name="content"></slot>
  </li>

  <li v-else-if="header" :class="classes" @click="handleClick">
    <slot></slot>
  </li>

  <li
    v-else-if="divide"
    role="separator"
    class="divider"
    @click="handleClick"
  ></li>

  <li v-else-if="safeHref" :class="classes">
    <a
      :href="safeHref"
      :target="target"
      :rel="target === '_blank' ? 'noopener noreferrer' : undefined"
      :data-qa-id="attrs['data-qa-id']"
      @click="handleClick"
    >
      <slot></slot>
    </a>
  </li>

  <li v-else :class="classes" @click="handleClick">
    <a
      v-if="disabled"
      aria-disabled="true"
      style="pointer-events: none"
      @click.prevent.stop
    >
      <slot></slot>
    </a>
    <a v-else href>
      <slot></slot>
    </a>
  </li>
</template>
